/**
 * DailyAdLimitCard — the workspace daily ad limit (Settings), the required
 * first section of the Spend limits card (SpendLimitsCard).
 *
 * The most this workspace's ad campaigns may be budgeted to spend in a day,
 * summed across every placement that can serve. Nothing can be deployed or
 * resumed without one, a placement that would take the total past it is
 * refused, and the spend-sync sweep pauses everything for the rest of the day
 * once today's actual spend reaches it.
 *
 * Only a workspace owner or admin can change it, from here — the server
 * refuses API keys, scheduled actions and every AI surface, and checks the
 * role again. Raising it (or setting it the first time) needs the amount typed
 * a second time; lowering it never does. Others see it read-only.
 *
 * $0 is a valid limit: "no ads may run", a deliberate freeze — distinct from
 * "not set". Setting $0 is a lowering (no typed confirmation, even the first
 * time: both stop everything); raising from $0 needs the amount typed again.
 */

import { useState } from 'react';
import { ApiError } from '@/lib/api';
import { useAdoptDailyAdLimit, usePutAdoptDailyAdLimit } from '../hooks/useAdvertisingOptimization';
import {
  GOOGLE_MAX_DAILY_OVERSPEND_FACTOR,
  GOOGLE_MONTHLY_CHARGE_DAYS,
  ZERO_DAILY_LIMIT_MESSAGE,
} from '../constants';

function usd(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * "15" / "15.50" / "0" → cents, or null when it is not an amount to the cent.
 * $0 is a real answer for a spend limit ("no ads may run"), so 0 parses.
 */
export function parseUsdToCents(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(Number(trimmed) * 100);
  return Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
}

function worstCase(limitCents: number): { dayCents: number; monthCents: number } {
  return {
    dayCents: limitCents * GOOGLE_MAX_DAILY_OVERSPEND_FACTOR,
    monthCents: Math.round(limitCents * GOOGLE_MONTHLY_CHARGE_DAYS),
  };
}

function WorstCaseLine({ limitCents }: { limitCents: number }) {
  const worst = worstCase(limitCents);
  return (
    <p className="text-sm text-muted-foreground">
      Google may spend up to 2× on a single day, and never more than 30.4× per month: up to{' '}
      <span className="font-medium text-foreground">{usd(worst.dayCents)}/day</span>,{' '}
      <span className="font-medium text-foreground">{usd(worst.monthCents)}/month</span>.
    </p>
  );
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'CONFIRMATION_REQUIRED':
        return 'Type the new amount a second time to confirm it.';
      case 'HUMAN_SESSION_REQUIRED':
        return 'The daily ad limit can only be changed from the Fias web app while signed in.';
      case 'OWNER_OR_ADMIN_REQUIRED':
        return 'Only a workspace owner or admin can change the daily ad limit.';
      default:
        return error.message;
    }
  }
  return error instanceof Error ? error.message : 'Saving the limit failed';
}

export function DailyAdLimitCard({ holderId }: { holderId: string }) {
  const limitQuery = useAdoptDailyAdLimit(holderId);
  const putLimit = usePutAdoptDailyAdLimit(holderId);
  const [amount, setAmount] = useState('');
  const [confirm, setConfirm] = useState('');

  if (limitQuery.isLoading) {
    return <section className="text-sm text-muted-foreground">Loading daily ad limit…</section>;
  }
  const limit = limitQuery.data;
  if (!limit) return null;

  const newCents = parseUsdToCents(amount);
  const inBounds =
    newCents !== null && newCents >= limit.bounds.minCents && newCents <= limit.bounds.maxCents;
  const isFirstSet = limit.limitCents === null;
  const isRaise = newCents !== null && limit.limitCents !== null && newCents > limit.limitCents;
  // A first set or a raise needs the amount typed again — except a first set
  // to $0: "not set" already stops everything, and $0 stops everything too.
  const needsConfirm = newCents !== null && (isRaise || (isFirstSet && newCents > 0));
  const unchanged = newCents !== null && newCents === limit.limitCents;
  const confirmMatches = newCents !== null && parseUsdToCents(confirm) === newCents;
  const lowersBelowRunning =
    newCents !== null && !isRaise && !unchanged && newCents < limit.committedCents;
  const canSave =
    limit.canEdit &&
    inBounds &&
    !unchanged &&
    (!needsConfirm || confirmMatches) &&
    !putLimit.isPending;

  const save = (): void => {
    if (newCents === null) return;
    putLimit.mutate(
      { dailyLimitCents: newCents, ...(needsConfirm ? { confirmCents: newCents } : {}) },
      {
        onSuccess: () => {
          setAmount('');
          setConfirm('');
        },
      },
    );
  };

  const result = putLimit.data;

  return (
    <section aria-labelledby="adopt-daily-ad-limit-title" className="flex flex-col gap-3">
      <div>
        <h3 id="adopt-daily-ad-limit-title" className="text-sm font-medium">
          Daily ad limit
        </h3>
        <p className="text-sm text-muted-foreground">
          The most this workspace&apos;s campaigns may be budgeted to spend in a day, across every
          placement. Only a workspace owner or admin can change it, here — never an AI assistant, an
          API key or a scheduled action.
        </p>
      </div>

      {limit.limitCents === null ? (
        <p className="text-sm text-rose-700" data-testid="daily-ad-limit-current">
          Not set. Nothing can be deployed or resumed until a daily ad limit is set.
        </p>
      ) : (
        <div className="flex flex-col gap-1" data-testid="daily-ad-limit-current">
          <p className="text-sm">
            Limit: <span className="font-semibold">{usd(limit.limitCents)}/day</span>
            {limit.updatedAt ? (
              <span className="text-muted-foreground">
                {' '}
                · last changed {new Date(limit.updatedAt).toLocaleString()}
              </span>
            ) : null}
          </p>
          {limit.limitCents === 0 ? (
            <p className="text-sm text-amber-700" data-testid="daily-ad-limit-zero">
              {ZERO_DAILY_LIMIT_MESSAGE} Nothing can be deployed or resumed until it is raised.
            </p>
          ) : (
            <WorstCaseLine limitCents={limit.limitCents} />
          )}
        </div>
      )}

      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Committed by running placements</dt>
        <dd>{usd(limit.committedCents)}/day</dd>
        <dt className="text-muted-foreground">Headroom</dt>
        <dd data-testid="daily-ad-limit-headroom">
          {limit.headroomCents === null ? '—' : `${usd(limit.headroomCents)}/day`}
        </dd>
        <dt className="text-muted-foreground">Spent today</dt>
        <dd>
          {limit.todaySpend
            ? `${usd(limit.todaySpend.cents)} (as of ${new Date(limit.todaySpend.observedAt).toLocaleTimeString()}${limit.todaySpend.fresh ? '' : ', may be out of date'})`
            : 'Not read yet'}
        </dd>
      </dl>

      {limit.canEdit ? (
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">
              {limit.limitCents === null ? 'Daily limit (USD)' : 'New daily limit (USD)'}
            </span>
            <input
              type="text"
              inputMode="decimal"
              className="w-40 rounded-md border border-border bg-background px-2 py-1.5"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setConfirm('');
              }}
              placeholder="20.00"
            />
          </label>
          {amount.trim() !== '' && !inBounds && (
            <p className="text-xs text-rose-600">
              Enter an amount between {usd(limit.bounds.minCents)} and {usd(limit.bounds.maxCents)}.
            </p>
          )}
          {inBounds &&
            newCents !== null &&
            !unchanged &&
            (newCents === 0 ? (
              <p className="text-xs text-amber-700">
                {ZERO_DAILY_LIMIT_MESSAGE} Nothing can be deployed or resumed until it is raised
                again, which needs the new amount typed twice.
              </p>
            ) : (
              <WorstCaseLine limitCents={newCents} />
            ))}
          {inBounds && needsConfirm && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Type the new amount to confirm</span>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="w-40 rounded-md border border-border bg-background px-2 py-1.5"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </label>
          )}
          {lowersBelowRunning && (
            <p className="text-xs text-amber-700">
              This is below what is running ({usd(limit.committedCents)}/day), so every live
              placement will be paused now.{' '}
              {newCents === 0
                ? 'They stay paused until the limit is raised.'
                : 'Resume the ones that fit afterwards.'}
            </p>
          )}
          <div>
            <button
              onClick={save}
              disabled={!canSave}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
            >
              {isFirstSet ? 'Set limit' : isRaise ? 'Raise limit' : 'Lower limit'}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Only a workspace owner or admin can change this limit.
        </p>
      )}

      {putLimit.isError && <p className="text-sm text-rose-600">{errorText(putLimit.error)}</p>}
      {result && putLimit.isSuccess && (
        <p className="text-sm text-emerald-700" role="status">
          {result.change.direction === 'unchanged'
            ? 'The limit is unchanged.'
            : result.change.limitCents === 0
              ? `Saved: $0/day. ${ZERO_DAILY_LIMIT_MESSAGE}`
              : `Saved: ${usd(result.change.limitCents)}/day.`}
          {result.change.pausedPlacements > 0
            ? ` ${result.change.pausedPlacements} running placement${result.change.pausedPlacements === 1 ? ' was' : 's were'} paused.`
            : ''}
          {result.channelPause.some((c) => !c.ok)
            ? ' Google has not confirmed every pause yet; it is retried automatically.'
            : ''}
          {result.ownerNotification.sent > 0 ? ' Workspace owners were emailed.' : ''}
        </p>
      )}
    </section>
  );
}
